import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import {
  INavigationLoader,
  NavigationDropdown,
  NavigationItem,
  NavigationLink
} from '@pharvio/core/ui';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthState } from '@pharvio/accounts/admin-account/data-access';
import {
  FEATURES,
  FeatureFlag,
  FeatureFlagService,
  OfflineModeService
} from '@pharvio/core/shared';
import { IFeatureState } from '@pharvio/core/shared';
import { FeaturesState } from '@pharvio/settings/data-access';
import { APP_ROUTES } from '../../app-routes';
import { PERMISSIONS } from '../constants/permissions.constant';
import { AdminPaths } from '../constants/routes/admin-routes';
import { CatalogPaths } from '../constants/routes/catalog-routes';
import { FinancePaths } from '../constants/routes/finance-routes';
import { ReportPaths } from '../constants/routes/reports-routes';
import { InventoryPaths } from '../constants/routes/inventory-routes';
import { CustomersPaths } from '../constants/routes/customers-routes';
import { OrderPaths } from '../constants/routes/orders-routes';
import {
  PayoutsPaths,
  SettingsPaths,
  TaxRulesPaths
} from '../constants/routes/settings-routes';
import {
  BookableServicesPaths,
  BookingsPaths
} from '../constants/routes/bookings-routes';
import {
  ExpenseCategoriesPaths,
  ExpensesPaths
} from '../constants/routes/expenses-routes';
import { hasPermission } from '@pharvio/core/shared';

@Injectable()
export class NavigationLoaderService implements INavigationLoader {
  private readonly _items = new BehaviorSubject<NavigationItem[]>([]);

  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly offlineMode = inject(OfflineModeService);

  /** The only destinations that work without a connection. */
  private readonly offlineRoutes = new Set<string>([
    `/${APP_ROUTES.Dashboard}`,
    `/${APP_ROUTES.POS}`,
    `/${APP_ROUTES.Orders}`,
    OrderPaths.Create
  ]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  /**
   * The resolved feature map, kept from the last rebuild.
   *
   * Held as a field rather than read through `selectSnapshot` inside a
   * `showWhen`: `FeaturesState.isVisible(key)` returns a plain function over
   * the **feature slice**, and `selectSnapshot` would hand it the root state
   * — `state.tenant` is undefined there, and the throw escapes `showWhen`
   * into the subscriber below, leaving the whole sidebar empty.
   */
  private features: IFeatureState[] = [];

  constructor(private readonly store: Store) {
    // The feature map joins permissions and connectivity as a rebuild
    // trigger: it resolves after boot, so a nav built once before it lands
    // would have the wrong answer for every feature-gated entry.
    combineLatest([
      this.store.select(AuthState.permissions),
      toObservable(this.offlineMode.isOffline),
      this.store.select(FeaturesState.tenantMap)
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([permissions, offline, featureMap]) => {
        this.features = featureMap.data.features;
        const items = this.filterItems(this.allItems(), permissions);
        this._items.next(offline ? this.filterOffline(items) : items);
      });
  }

  /** Keeps only links that work offline; drops sections left empty. */
  private filterOffline(items: NavigationItem[]): NavigationItem[] {
    const keep = (
      children: (NavigationLink | NavigationDropdown)[]
    ): (NavigationLink | NavigationDropdown)[] =>
      children.flatMap((c): (NavigationLink | NavigationDropdown)[] => {
        if (c.type === 'link') {
          return this.offlineRoutes.has(c.route) ? [c] : [];
        }
        const inner = keep(c.children);
        return inner.length ? [{ ...c, children: inner }] : [];
      });

    return items.flatMap((item): NavigationItem[] => {
      if (item.type === 'link') {
        return this.offlineRoutes.has(item.route) ? [item] : [];
      }
      if (item.type === 'subheading' || item.type === 'dropdown') {
        const children = keep(item.children);
        return children.length ? [{ ...item, children }] : [];
      }
      return [];
    });
  }

  /**
   * Bookings sit behind the `bookable_services` feature as well as the
   * permissions. Hide the entry rather than letting the API refuse.
   *
   * Fails open, like every other feature gate: an unknown key — the map never
   * loaded, or the server doesn't know it — reads as visible, because a
   * missing map is a transport problem and hiding nav over one is worse than
   * showing a page the server refuses anyway.
   */
  private bookingsVisible(): boolean {
    const state = this.features.find(
      (f) => f.key === FEATURES.BOOKABLE_SERVICES
    )?.state;
    return state !== 'hidden';
  }

  /**
   * Evaluates one item's `showWhen`, defaulting to visible if it throws.
   *
   * A gate reads app state, so it can fail in ways the nav cannot predict.
   * Without this the throw escapes the subscriber that builds the list and
   * `_items` is never pushed — one broken gate empties the entire sidebar,
   * which is exactly how this was first shipped.
   */
  private isShown(item: { showWhen?: () => boolean }): boolean {
    if (!item.showWhen) return true;
    try {
      return item.showWhen();
    } catch {
      return true;
    }
  }

  private canAccess(userPerms: string[], required?: string[]): boolean {
    if (!required?.length) return true;
    return hasPermission(userPerms, required);
  }

  private filterChildren(
    children: (NavigationLink | NavigationDropdown)[],
    userPerms: string[]
  ): (NavigationLink | NavigationDropdown)[] {
    return children.filter((c) => {
      const canShow = this.isShown(c);

      if (!canShow) return false;
      if (!this.canAccess(userPerms, c.permissions)) return false;
      if (c.type === 'dropdown') {
        c = { ...c, children: this.filterChildren(c.children, userPerms) };
        return c.children.length > 0;
      }
      return true;
    });
  }

  private filterItems(
    items: NavigationItem[],
    userPerms: string[]
  ): NavigationItem[] {
    const filtered: NavigationItem[] = [];
    for (const item of items) {
      const canShow = this.isShown(item);
      if (item.type === 'link') {
        if (canShow && this.canAccess(userPerms, item.permissions))
          filtered.push(item);
      } else if (item.type === 'subheading') {
        const children = this.filterChildren(item.children, userPerms);
        if (canShow && children.length) filtered.push({ ...item, children });
      } else if (item.type === 'dropdown') {
        if (!this.canAccess(userPerms, item.permissions)) continue;
        const children = this.filterChildren(item.children, userPerms);
        if (canShow && children.length) filtered.push({ ...item, children });
      } else {
        if (canShow) {
          filtered.push(item);
        }
      }
    }
    return filtered;
  }

  private allItems(): NavigationItem[] {
    return [
      {
        type: 'link',
        label: 'Dashboard',
        route: `/${APP_ROUTES.Dashboard}`,
        icon: 'mat:dashboard',
        routerLinkActiveOptions: { exact: true }
      },
      {
        type: 'subheading',
        label: 'Office',
        children: [
          {
            type: 'link',
            label: 'POS / Sales',
            route: `/${APP_ROUTES.POS}`,
            icon: 'mat:point_of_sale',
            permissions: [PERMISSIONS.ORDERS.CREATE]
          },
          {
            type: 'link',
            label: 'Open Shifts',
            route: FinancePaths.Shifts,
            icon: 'mat:schedule',
            permissions: [PERMISSIONS.SHIFTS.READ]
          },
          {
            type: 'link',
            label: 'Orders',
            route: `/${APP_ROUTES.Orders}`,
            icon: 'mat:receipt_long',
            permissions: [PERMISSIONS.ORDERS.READ]
          },
          {
            type: 'link',
            label: 'Create Order',
            route: OrderPaths.Create,
            icon: 'mat:add_circle_outline',
            permissions: [PERMISSIONS.ORDERS.CREATE]
          },
          {
            type: 'link',
            label: 'Prescriptions',
            route: `/${APP_ROUTES.Prescriptions}`,
            icon: 'mat:description',
            permissions: [PERMISSIONS.PRESCRIPTIONS.READ]
          },
          {
            type: 'link',
            label: 'Returns',
            route: `/${APP_ROUTES.Returns}`,
            icon: 'mat:assignment_return',
            permissions: [PERMISSIONS.RETURNS.READ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Services & Bookings',
        children: [
          {
            type: 'link',
            label: 'Bookings',
            route: BookingsPaths.Base,
            icon: 'mat:event_available',
            permissions: [PERMISSIONS.BOOKINGS.READ],
            // Not exact: the detail page lives under /bookings and should
            // keep the item highlighted.
            routerLinkActiveOptions: { exact: false },
            showWhen: () => this.bookingsVisible()
          },
          {
            type: 'link',
            label: 'Bookable Services',
            route: BookableServicesPaths.Base,
            icon: 'mat:design_services',
            permissions: [PERMISSIONS.BOOKABLE_SERVICES.READ],
            showWhen: () => this.bookingsVisible()
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Catalog',
        // icon: 'mat:medication',
        children: [
          {
            type: 'link',
            label: 'Categories',
            route: CatalogPaths.Categories,
            permissions: [PERMISSIONS.CATEGORIES.READ],
            icon: 'mat:category'
          },
          {
            type: 'link',
            label: 'Tags',
            route: CatalogPaths.Tags,
            permissions: [PERMISSIONS.CATEGORIES.READ],
            icon: 'mat:label'
          },
          {
            type: 'link',
            label: 'Products',
            route: CatalogPaths.Products,
            permissions: [PERMISSIONS.PRODUCTS.READ],
            routerLinkActiveOptions: { exact: true },
            icon: 'mat:inventory_2'
          },
          {
            type: 'link',
            label: 'Products Jobs',
            route: CatalogPaths.ImportJobs,
            permissions: [PERMISSIONS.PRODUCTS.IMPORT_READ],
            showWhen: () =>
              this.featureFlagService.isEnabled(FeatureFlag.PRODUCT_IMPORT),
            routerLinkActiveOptions: { exact: true },
            icon: 'mat:inventory_2'
          },
          {
            type: 'link',
            label: 'Featured Products',
            route: CatalogPaths.FeaturedProducts,
            permissions: [PERMISSIONS.PRODUCTS.UPDATE],
            icon: 'mat:star'
          },
          {
            type: 'link',
            label: 'Batches & Expiry',
            route: CatalogPaths.Batches,
            permissions: [PERMISSIONS.BATCHES.READ],
            icon: 'mat:event'
          },
          {
            type: 'link',
            label: 'Suppliers',
            route: CatalogPaths.Suppliers,
            permissions: [PERMISSIONS.SUPPLIERS.READ],
            icon: 'mat:local_shipping'
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Inventory',
        // icon: 'mat:inventory_2',
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: InventoryPaths.Dashboard,
            permissions: [PERMISSIONS.INVENTORY.READ],
            routerLinkActiveOptions: { exact: true },
            icon: 'mat:dashboard'
          },
          {
            type: 'link',
            label: 'Stock on Hand',
            route: InventoryPaths.Stock,
            permissions: [PERMISSIONS.INVENTORY.READ],
            icon: 'mat:assessment'
          },
          {
            type: 'link',
            label: 'Purchase Orders',
            route: InventoryPaths.PurchaseOrders,
            permissions: [PERMISSIONS.PURCHASE_ORDERS.READ],
            icon: 'mat:post_add'
          },
          {
            type: 'link',
            label: 'Goods Received',
            route: InventoryPaths.Grn,
            permissions: [PERMISSIONS.GRN.READ],
            icon: 'mat:move_to_inbox'
          },
          {
            type: 'link',
            label: 'Stock Transfers',
            route: InventoryPaths.Transfers,
            permissions: [PERMISSIONS.INVENTORY.TRANSFER],
            icon: 'mat:swap_horiz'
          },
          {
            type: 'link',
            label: 'Stock Takes',
            route: InventoryPaths.StockTakes,
            permissions: [PERMISSIONS.INVENTORY.STOCK_TAKE],
            icon: 'mat:assignment_turned_in'
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Customers',
        // icon: 'mat:people_alt',
        children: [
          {
            type: 'link',
            label: 'Customers',
            route: CustomersPaths.List,
            permissions: [PERMISSIONS.CUSTOMERS.READ],
            routerLinkActiveOptions: { exact: true },
            icon: 'mat:people'
          },
          {
            type: 'link',
            label: 'Loyalty',
            route: CustomersPaths.Loyalty,
            icon: 'mat:loyalty',
            permissions: [PERMISSIONS.LOYALTY.READ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Accounting',
        children: [
          {
            type: 'link',
            label: 'Expenses',
            route: ExpensesPaths.Base,
            icon: 'mat:receipt_long',
            permissions: [PERMISSIONS.EXPENSES.READ],
            // Not exact: the detail, import and running-cost pages are all
            // under /expenses and should keep the item highlighted.
            routerLinkActiveOptions: { exact: false }
          },
          {
            // Visible with `expenses.read`; editing needs
            // `expense_categories.manage`, which the page gates itself.
            type: 'link',
            label: 'Expense Categories',
            route: ExpenseCategoriesPaths.Base,
            icon: 'mat:category',
            permissions: [PERMISSIONS.EXPENSES.READ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Finance',
        // icon: 'mat:account_balance',
        children: [
          {
            type: 'dropdown',
            label: 'Reports',
            icon: 'mat:bar_chart',
            permissions: [PERMISSIONS.REPORTS.READ],
            children: [
              {
                type: 'link',
                label: 'Daily Sales',
                route: ReportPaths.Sales,
                icon: 'mat:show_chart',
                permissions: [PERMISSIONS.REPORTS.READ],
                queryParamsHandling: 'merge'
              },
              {
                type: 'link',
                label: 'Top Products',
                route: ReportPaths.TopItems,
                icon: 'mat:trending_up',
                permissions: [PERMISSIONS.REPORTS.READ],
                queryParamsHandling: 'merge'
              },
              {
                type: 'link',
                label: 'Inventory',
                route: ReportPaths.Valuation,
                icon: 'mat:inventory_2',
                permissions: [PERMISSIONS.REPORTS.READ],
                queryParamsHandling: 'merge'
              },
              {
                type: 'link',
                label: 'Tax',
                route: ReportPaths.Tax,
                icon: 'mat:account_balance',
                permissions: [PERMISSIONS.REPORTS.READ],
                queryParamsHandling: 'merge'
              },
              {
                type: 'link',
                label: 'Customer LTV',
                route: ReportPaths.Ltv,
                icon: 'mat:people',
                permissions: [PERMISSIONS.REPORTS.READ],
                queryParamsHandling: 'merge'
              },
              {
                type: 'link',
                label: 'Generated Reports',
                route: ReportPaths.Artifacts,
                icon: 'mat:download',
                permissions: [PERMISSIONS.REPORTS.DOWNLOAD_ARTIFACT]
              }
            ]
          },
          {
            type: 'link',
            label: 'Invoices',
            route: FinancePaths.Invoices,
            icon: 'mat:receipt',
            permissions: [PERMISSIONS.INVOICES.READ]
          },
          {
            type: 'link',
            label: 'Refund Tickets',
            route: FinancePaths.RefundTickets,
            icon: 'mat:money_off',
            permissions: [PERMISSIONS.REFUNDS.READ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Admin',
        // icon: 'mat:admin_panel_settings',
        children: [
          {
            type: 'link',
            label: 'Users',
            route: AdminPaths.Users,
            icon: 'mat:manage_accounts',
            permissions: [PERMISSIONS.USERS.READ],
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Roles',
            route: AdminPaths.Roles,
            icon: 'mat:security',
            permissions: [PERMISSIONS.ROLES.READ]
          },
          {
            type: 'link',
            label: 'Branches',
            route: AdminPaths.Branches,
            icon: 'mat:store',
            permissions: [PERMISSIONS.BRANCHES.READ]
          },
          {
            type: 'link',
            label: 'Audit Log',
            route: AdminPaths.AuditLog,
            icon: 'mat:fact_check',
            permissions: [PERMISSIONS.AUDIT.READ_TENANT]
          },
          {
            type: 'link',
            label: 'Storefront',
            route: AdminPaths.Storefront,
            icon: 'mat:public',
            permissions: [PERMISSIONS.STOREFRONT.READ]
          },
          {
            type: 'link',
            label: 'Payout Accounts',
            route: PayoutsPaths.Base,
            icon: 'mat:account_balance',
            permissions: [PERMISSIONS.PAYOUT_ACCOUNTS.READ]
          },
          {
            type: 'link',
            label: 'Tax Rules',
            route: TaxRulesPaths.Base,
            icon: 'mat:calculate',
            permissions: [PERMISSIONS.SETTINGS.TAX_CLASSES_READ]
          },
          {
            type: 'link',
            label: 'Subscription',
            route: AdminPaths.Subscription,
            icon: 'mat:card_membership',
            permissions: [PERMISSIONS.PLATFORM.SUBSCRIPTIONS_READ]
          },
          // {
          //   type: 'link',
          //   label: 'AI Assistant',
          //   route: `/${APP_ROUTES.Assistant}`,
          //   icon: 'mat:smart_toy',
          //   permissions: [PERMISSIONS.AI.QUERY]
          // },
          {
            type: 'dropdown',
            label: 'Settings',
            icon: 'mat:settings',
            permissions: [
              PERMISSIONS.SETTINGS.ORGANISATION_MANAGE,
              PERMISSIONS.LOYALTY.CONFIGURE
            ],
            children: [
              {
                type: 'link',
                label: 'System Settings',
                route: AdminPaths.Settings,
                icon: 'mat:settings',
                permissions: [PERMISSIONS.SETTINGS.ORGANISATION_MANAGE]
              },
              {
                type: 'link',
                label: 'Loyalty Settings',
                route: SettingsPaths.Loyalty,
                icon: 'mat:loyalty',
                permissions: [PERMISSIONS.LOYALTY.CONFIGURE]
              },
              {
                type: 'link',
                label: 'Notifications',
                route: SettingsPaths.Notifications,
                icon: 'mat:notifications',
                permissions: [PERMISSIONS.SETTINGS.ORGANISATION_MANAGE]
              }
            ]
          }
        ]
      }
    ];
  }
}
